import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { request } from '../api/client';
import { useAuthStore } from '../store/auth';
import { RedirectToLogin } from './LoginModal';

export function RequireAuth({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const syncUser = useAuthStore((s) => s.syncUser);
  const location = useLocation();

  // Permissions are captured at login and persisted client-side. Re-fetch the
  // live profile so role/permission changes made by an admin take effect on
  // page reload or tab-focus — without forcing a full re-login. The backend
  // resolves permissions fresh per request, so /auth/me is always current.
  const { data: me } = useQuery({
    queryKey: ['auth-me'],
    queryFn: () => request<any>({ url: '/auth/me' }),
    enabled: !!user,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!me) return;
    syncUser({
      id: String(me.id),
      fullName: me.fullName,
      role: me.role,
      roles: me.roles ?? [me.role],
      permissions: me.permissions ?? [],
      territoryId: me.territoryId ?? null,
      onboardingRequired: !!me.onboardingRequired,
    });
  }, [me, syncUser]);

  if (!user) return <RedirectToLogin from={location.pathname + location.search} />;
  // A farmer who has not finished signing up has nothing to do in the app yet (they hold no
  // permissions) — every app route sends them to the profile form.
  if (user.onboardingRequired) return <Navigate to="/welcome" replace />;
  return <>{children}</>;
}
